import { TableColumn } from '@shared/components/data-table/data-table.component';
import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import type { PurchaseRequestRecord } from './mock-data/purchase-request.mock-data';
import { PurchaseRequestResultDTO } from '@app/api/purchase-service';

// Purchase Request Status Enum
export enum PurchaseRequestStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  STOPPED = 'STOPPED',
  CANCELLED = 'CANCELLED',
  PENDING = 'PENDING',
  PARTIALLY_ORDERED = 'PARTIALLY_ORDERED',
  PARTIALLY_RECEIVED = 'PARTIALLY_RECEIVED',
  ORDERED = 'ORDERED',
  ISSUED = 'ISSUED',
  TRANSFERRED = 'TRANSFERRED',
  RECEIVED = 'RECEIVED',
}

// Purchase Request Purpose Enum
export enum PurchaseRequestPurpose {
  PURCHASE = 'PURCHASE',
  MATERIAL_TRANSFER = 'MATERIAL_TRANSFER',
  MATERIAL_ISSUE = 'MATERIAL_ISSUE',
  MANUFACTURE = 'MANUFACTURE',
  CUSTOMER_PROVIDED = 'CUSTOMER_PROVIDED',
}

// Purchase Request Record Interface - Imported from mock data
export type { PurchaseRequestRecord } from './mock-data/purchase-request.mock-data';

// Purchase Request Response Interface
export interface PurchaseRequestResponse {
  pageContent: PurchaseRequestRecord[];
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
  empty: boolean;
  numberOfElements: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// Default Configuration
export const PURCHASE_REQUEST_DEFAULT_CONFIG = {
  PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  DEFAULT_SORT_FIELD: 'creationDate',
  DEFAULT_SORT_ORDER: 'desc' as 'asc' | 'desc',
};

// Status Options for Dropdown
export const PURCHASE_REQUEST_STATUS_OPTIONS = [
  {
    label: 'purchase_request.status.pending_approval',
    value: 'PENDING',
  },
  { label: 'purchase_request.status.approved', value: 'APPROVED' },
  { label: 'purchase_request.status.rejected', value: 'REJECTED' },
];

// Purpose Options for Dropdown
export const PURCHASE_REQUEST_PURPOSE_OPTIONS = [
  { label: 'Purchase', value: PurchaseRequestPurpose.PURCHASE },
  {
    label: 'Material Transfer',
    value: PurchaseRequestPurpose.MATERIAL_TRANSFER,
  },
  { label: 'Material Issue', value: PurchaseRequestPurpose.MATERIAL_ISSUE },
  { label: 'Manufacture', value: PurchaseRequestPurpose.MANUFACTURE },
  {
    label: 'Customer Provided',
    value: PurchaseRequestPurpose.CUSTOMER_PROVIDED,
  },
];

// Company Options (Mock Data)
export const COMPANY_OPTIONS = [
  { label: 'ABC Corporation', value: 'ABC_CORP' },
  { label: 'XYZ Industries', value: 'XYZ_IND' },
  { label: 'Tech Solutions Ltd', value: 'TECH_SOL' },
  { label: 'Global Manufacturing', value: 'GLOBAL_MFG' },
  { label: 'Innovation Systems', value: 'INNOV_SYS' },
];

// Search Form Configuration
export const PURCHASE_REQUEST_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'series',
    label: 'purchase_request.search.fields.series',
    type: 'text',
    placeholder: 'purchase_request.search.placeholders.enter_series',
  },
  {
    key: 'title',
    label: 'purchase_request.search.fields.title',
    type: 'text',
    placeholder: 'purchase_request.search.placeholders.enter_title',
  },
  {
    key: 'status',
    label: 'purchase_request.search.fields.status',
    type: 'select',
    placeholder: 'purchase_request.search.placeholders.select_status',
    options: [
      { label: 'purchase_request.status.pending_approval', value: 'PENDING' },
      { label: 'purchase_request.status.approved', value: 'APPROVED' },
      { label: 'purchase_request.status.rejected', value: 'REJECTED' },
    ],
  },
  // {
  //   key: 'transactionDate',
  //   label: 'purchase_request.search.fields.transaction_date',
  //   type: 'dateRange',
  //   range: {
  //     startKey: 'beginTransactionDate',
  //     endKey: 'endTransactionDate',
  //     startLabel: 'purchase_request.search.labels.transaction_date_start',
  //     endLabel: 'purchase_request.search.labels.transaction_date_end'
  //   },
  // },
  // {
  //   key: 'scheduleDate',
  //   label: 'purchase_request.search.fields.schedule_date',
  //   type: 'dateRange',
  //   range: {
  //     startKey: 'beginScheduleDate',
  //     endKey: 'endScheduleDate',
  //     startLabel: 'purchase_request.search.labels.schedule_date_start',
  //     endLabel: 'purchase_request.search.labels.schedule_date_end'
  //   },
  // },
];

// ============================================================================
// TABLE COLUMN CONFIGURATIONS
// ============================================================================

/**
 * Default column property names (shown on first load)
 * Update these constants to change column property names
 */
export const PURCHASE_REQUEST_DEFAULT_COLUMNS = {
  SERIES: 'series',
  TITLE: 'title',
  STATUS: 'status',
  PURCHASE_ORDER: 'purchase_order',
  SCHEDULE_DATE: 'scheduleDate',
  CREATION_DATE: 'creationDate',
} as const;

/**
 * Action column property name
 */
export const PURCHASE_REQUEST_ACTION_COLUMN = {
  ACTIONS: 'actions',
} as const;

/**
 * Default columns order (shown on first load)
 * Update this array to change the default column order
 */
export const PURCHASE_REQUEST_DEFAULT_COLUMNS_ORDER: string[] = [
  PURCHASE_REQUEST_DEFAULT_COLUMNS.SERIES,
  PURCHASE_REQUEST_DEFAULT_COLUMNS.TITLE,
  PURCHASE_REQUEST_DEFAULT_COLUMNS.STATUS,
  PURCHASE_REQUEST_DEFAULT_COLUMNS.PURCHASE_ORDER,
  PURCHASE_REQUEST_DEFAULT_COLUMNS.SCHEDULE_DATE,
  PURCHASE_REQUEST_DEFAULT_COLUMNS.CREATION_DATE,
];

/**
 * Flexible columns array (can be shown/hidden via column selector)
 * Update this array to change which columns are flexible
 */
export const PURCHASE_REQUEST_FLEXIBLE_COLUMNS_ORDER: string[] = [];

export const PURCHASE_REQUEST_TABLE_COLUMNS: TableColumn[] = [
  {
    label: 'purchase_request.table.columns.series',
    prop: PURCHASE_REQUEST_DEFAULT_COLUMNS.SERIES,
    type: 'text',
    width: 120,
    align: 'center',
  },
  {
    label: 'purchase_request.table.columns.title',
    prop: PURCHASE_REQUEST_DEFAULT_COLUMNS.TITLE,
    type: 'text',
    width: 300,
    align: 'left',
  },
  {
    label: 'purchase_request.table.columns.status',
    prop: PURCHASE_REQUEST_DEFAULT_COLUMNS.STATUS,
    type: 'custom',
    width: 150,
    align: 'center',
  },
  {
    label: 'purchase_request.table.columns.purchase_order',
    prop: PURCHASE_REQUEST_DEFAULT_COLUMNS.PURCHASE_ORDER,
    type: 'custom',
    width: 150,
    align: 'center',
  },
  {
    label: 'purchase_request.table.columns.schedule_date',
    prop: PURCHASE_REQUEST_DEFAULT_COLUMNS.SCHEDULE_DATE,
    type: 'custom',
    width: 120,
    align: 'center',
  },
  {
    label: 'purchase_request.table.columns.creation_date',
    prop: PURCHASE_REQUEST_DEFAULT_COLUMNS.CREATION_DATE,
    type: 'custom',
    width: 120,
    align: 'center',
  },
  {
    label: 'purchase_request.table.columns.actions',
    prop: PURCHASE_REQUEST_ACTION_COLUMN.ACTIONS,
    type: 'actions',
    width: 180,
    align: 'center',
  },
];

// Status Severity Mapping
export const PURCHASE_REQUEST_STATUS_SEVERITY: {
  [key: string]: 'success' | 'info' | 'warning' | 'danger' | 'secondary';
} = {
  // API statuses (PurchaseRequestResultDTO.StatusEnum)
  [PurchaseRequestResultDTO.StatusEnum.Pending]: 'warning',
  [PurchaseRequestResultDTO.StatusEnum.Approved]: 'success',
  [PurchaseRequestResultDTO.StatusEnum.Rejected]: 'danger',
  // Legacy statuses for backward compatibility
  PENDING_APPROVAL: 'warning',
  [PurchaseRequestStatus.DRAFT]: 'secondary',
  [PurchaseRequestStatus.SUBMITTED]: 'info',
  [PurchaseRequestStatus.STOPPED]: 'warning',
  [PurchaseRequestStatus.CANCELLED]: 'danger',
  [PurchaseRequestStatus.PARTIALLY_ORDERED]: 'warning',
  [PurchaseRequestStatus.PARTIALLY_RECEIVED]: 'warning',
  [PurchaseRequestStatus.ORDERED]: 'info',
  [PurchaseRequestStatus.ISSUED]: 'info',
  [PurchaseRequestStatus.TRANSFERRED]: 'info',
  [PurchaseRequestStatus.RECEIVED]: 'success',
};

// Purpose Severity Mapping
export const PURCHASE_REQUEST_PURPOSE_SEVERITY: {
  [key: string]: 'success' | 'info' | 'warning' | 'danger' | 'secondary';
} = {
  [PurchaseRequestPurpose.PURCHASE]: 'info',
  [PurchaseRequestPurpose.MATERIAL_TRANSFER]: 'warning',
  [PurchaseRequestPurpose.MATERIAL_ISSUE]: 'warning',
  [PurchaseRequestPurpose.MANUFACTURE]: 'info',
  [PurchaseRequestPurpose.CUSTOMER_PROVIDED]: 'success',
};
